import React, { useState } from 'react';
import './InteractiveEmulationSection.css';

const InteractiveEmulationSection = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCase, setSelectedCase] = useState('pump');
  const [activeTab, setActiveTab] = useState('additional');

  const handleClickableElement = (elementName) => {
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const handleCaseClick = (caseId) => {
    setSelectedCase(caseId);
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    handleClickableElement(`tab-${tabId}`);
  };

  // Pump case data (industrial equipment)
  const pumpMockData = [
    { id: 1, timestamp: 1725123456789, ax: '0.1234', ay: '-0.0567', az: '9.8123', gx: '0.0123', gy: '-0.0045', gz: '0.0089', label: 'normal_operation' },
    { id: 2, timestamp: 1725123456799, ax: '0.1456', ay: '-0.0789', az: '9.8234', gx: '0.0156', gy: '-0.0067', gz: '0.0123', label: 'normal_operation' },
    { id: 3, timestamp: 1725123456809, ax: '0.1678', ay: '-0.0901', az: '9.8345', gx: '0.0189', gy: '-0.0089', gz: '0.0156', label: 'normal_operation' },
    { id: 4, timestamp: 1725123456819, ax: '2.1234', ay: '-0.6789', az: '10.1234', gx: '0.1234', gy: '-0.0567', gz: '0.0890', label: 'broken_pump_high_vibration' },
    { id: 5, timestamp: 1725123456829, ax: '2.4567', ay: '-0.7890', az: '10.2345', gx: '0.1456', gy: '-0.0678', gz: '0.1012', label: 'broken_pump_high_vibration' }
  ];

  // ESP32 case data (vehicle movement)
  const esp32MockData = [
    { id: 1, timestamp: 1725123456789, ax: '0.1234', ay: '-0.0567', az: '9.8123', gx: '0.0123', gy: '-0.0045', gz: '0.0089', label: 'straight_driving' },
    { id: 2, timestamp: 1725123456799, ax: '0.1456', ay: '-0.0789', az: '9.8234', gx: '0.0156', gy: '-0.0067', gz: '0.0123', label: 'straight_driving' },
    { id: 3, timestamp: 1725123456809, ax: '0.1678', ay: '-0.0901', az: '9.8345', gx: '0.0189', gy: '-0.0089', gz: '0.0156', label: 'straight_driving' },
    { id: 4, timestamp: 1725123456819, ax: '2.1234', ay: '-0.6789', az: '9.1234', gx: '0.1234', gy: '-0.1567', gz: '0.0890', label: 'turn_left' },
    { id: 5, timestamp: 1725123456829, ax: '2.4567', ay: '-0.7890', az: '9.2345', gx: '0.1456', gy: '-0.1678', gz: '0.1012', label: 'turn_left' }
  ];

  // Get current data based on selected case
  const getCurrentData = () => {
    return selectedCase === 'pump' ? pumpMockData : esp32MockData;
  };

  // Get current case title
  const getCurrentCaseTitle = () => {
    return selectedCase === 'pump' 
      ? 'Predictive Maintenance Pump SN-XX - Datasets Preview'
      : 'MPU-6050 ESP32 - Datasets Preview';
  };

  // Get current format
  const getCurrentFormat = () => {
    return selectedCase === 'pump' ? 'CSV' : 'Parquet';
  };

  // Get current scenarios
  const getCurrentScenarios = () => {
    if (selectedCase === 'pump') {
      return [
        {
          title: 'Normal Operation',
          description: 'Standard operating conditions with IMU readings within normal ranges (±0.25g acceleration, ±0.05 rad/s angular velocity)'
        },
        {
          title: 'Broken Pump High Vibration',
          description: 'Pump failure causing excessive vibration levels (1-4g acceleration, ±1 rad/s angular velocity) detectable by IMU sensors'
        },
        {
          title: 'Low Oil Pressure',
          description: 'Insufficient lubrication causing increased friction and irregular motion patterns (±0.75g acceleration, ±0.15 rad/s angular velocity)'
        },
        {
          title: 'Overheating Condition',
          description: 'Thermal expansion effects causing mechanical stress and vibration changes (±0.4g acceleration, ±0.1 rad/s angular velocity)'
        }
      ];
    } else {
      return [
        {
          title: 'Straight Driving',
          description: 'Normal forward movement with minimal lateral acceleration and steady angular velocity readings (±0.2g acceleration, ±0.05 rad/s angular velocity)'
        },
        {
          title: 'Turn Left',
          description: 'Left turn maneuver with characteristic lateral acceleration and yaw rate changes (2-3g lateral acceleration, ±0.15 rad/s angular velocity)'
        },
        {
          title: 'Turn Right',
          description: 'Right turn maneuver with opposite lateral acceleration and yaw rate patterns (2-3g lateral acceleration, ±0.15 rad/s angular velocity)'
        },
        {
          title: 'Braking Event',
          description: 'Vehicle deceleration with forward acceleration spike and minimal lateral movement (3-5g forward acceleration, ±0.1 rad/s angular velocity)'
        }
      ];
    }
  };

  return (
    <section className="interactive-emulation-section">
      <div className="emulation-container">
        <div className="emulation-header">
          <div className="emulation-header-left">
            <div className="emulation-title">
              <h1>Simulated Experience Studio</h1>
              <span className="emulation-subtitle">Synthetic Dataset Generator</span>
            </div>
          </div>
          <div className="emulation-header-right">
            <div className="emulation-user-dropdown">
              <button 
                onClick={() => handleClickableElement('user-dropdown')}
                className="emulation-user-dropdown-button"
              >
                <span className="emulation-user-email">aol@vossmoos.de</span>
                <span className="emulation-dropdown-icon">▼</span>
              </button>
            </div>
          </div>
        </div>

        <div className="emulation-main">
          <div className="emulation-sidebar">
            <div className="emulation-sidebar-section">
              <h3 className="emulation-sidebar-title">CASES</h3>
              
              <div 
                className={`emulation-case-item ${selectedCase === 'pump' ? 'selected' : ''}`}
                onClick={() => handleCaseClick('pump')}
              >
                <div className="emulation-case-bar status-generated">
                  <div className="emulation-case-bar-content">
                    <div className="emulation-case-name">
                      Predictive Maintenance Pump SN-XX <span className="emulation-case-row-count">• 1.1M rows</span>
                    </div>
                  </div>
                </div>
              </div>

              <div 
                className={`emulation-case-item ${selectedCase === 'esp32' ? 'selected' : ''}`}
                onClick={() => handleCaseClick('esp32')}
              >
                <div className="emulation-case-bar status-generated">
                  <div className="emulation-case-bar-content">
                    <div className="emulation-case-name">
                      MPU-6050 ESP32 <span className="emulation-case-row-count">• 100.0K rows</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <button 
                className="emulation-action-button" 
                onClick={() => handleClickableElement('add-case')}
              >
                <span className="emulation-action-icon">+</span>
                <span className="emulation-action-text">Add Case</span>
              </button>
            </div>
          </div>

          <div className="emulation-content">
            <div className="emulation-workspace">
              <div className="emulation-workspace-content">
                <div className="emulation-data-table-section">
                  <div className="emulation-table-header">
                    <h3 className="emulation-table-title">{getCurrentCaseTitle()}</h3>
                    <div className="emulation-table-header-buttons">
                      <button 
                        className="emulation-action-button emulation-download-btn"
                        onClick={() => handleClickableElement('download')}
                      >
                        <span className="emulation-action-icon">⬇</span>
                        <span className="emulation-action-text">Download</span>
                        <span className="emulation-action-size">{selectedCase === 'pump' ? '546 Mb' : '234 Mb'}</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="emulation-tabs-container">
                    <div className="emulation-tabs">
                      <button
                        className={`emulation-tab ${activeTab === 'initial' ? 'active' : ''}`}
                        onClick={() => handleTabClick('initial')}
                      >
                        Dataset: Initial Generation
                      </button>
                      <button
                        className={`emulation-tab ${activeTab === 'additional' ? 'active' : ''}`}
                        onClick={() => handleTabClick('additional')}
                      >
                        Dataset: Additional Set 01
                      </button>
                    </div>
                    
                    <div className="emulation-tab-content">
                      <div className="emulation-data-table-container">
                        <table className="emulation-data-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>TIMESTAMP (MS)</th>
                              <th>AX</th>
                              <th>AY</th>
                              <th>AZ</th>
                              <th>GX</th>
                              <th>GY</th>
                              <th>GZ</th>
                              <th>LABEL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getCurrentData().map((row, index) => (
                              <tr key={index}>
                                <td>{row.id}</td>
                                <td>{row.timestamp}</td>
                                <td>{row.ax}</td>
                                <td>{row.ay}</td>
                                <td>{row.az}</td>
                                <td>{row.gx}</td>
                                <td>{row.gy}</td>
                                <td>{row.gz}</td>
                                <td>{row.label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="emulation-dashboard-widgets">
                  <div className="emulation-widget emulation-case-details">
                    <h4>Case Details</h4>
                    <div className="emulation-widget-content">
                      <div 
                        className="emulation-case-detail-item"
                        onClick={() => handleClickableElement('datasets-count')}
                      >
                        <div className="emulation-case-detail-number">2</div>
                        <div className="emulation-case-detail-label">DATASETS</div>
                      </div>
                      <div 
                        className="emulation-case-detail-item"
                        onClick={() => handleClickableElement('format-type')}
                      >
                        <div className="emulation-case-detail-number">{getCurrentFormat()}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="emulation-widget emulation-scenarios">
                    <h4>Scenarios</h4>
                    <div className="emulation-widget-content">
                      <div className="emulation-scenario-list">
                        {getCurrentScenarios().map((scenario, index) => (
                          <div 
                            key={index}
                            className="emulation-scenario-item"
                            onClick={() => handleClickableElement(`scenario-${scenario.title.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            <span className="emulation-scenario-title">{scenario.title}</span>: {scenario.description}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="emulation-popup-overlay" onClick={closePopup}>
          <div className="emulation-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="emulation-popup-header">
              <h3>Would you like to synthesize the data for your equipment?</h3>
              <button className="emulation-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="emulation-popup-body">
              <p>Request the access to your cloud instance of the tool with the <strong>Free Forever</strong> plan and start to model and generate your synthetic datasets with AI in minutes instead of weeks</p>
            </div>
            <div className="emulation-popup-actions">
              <button 
                className="emulation-action-button emulation-popup-proceed-btn"
                onClick={() => {
                  window.open('https://calendly.com/aol-vossmoos/realistic-synthetic-datasets', '_blank');
                  closePopup();
                }}
              >
                <span className="emulation-action-text">Proceed</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default InteractiveEmulationSection;
